import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { Search } from "lucide-react";
import { BriefcaseFilled, CompassNorthwestFilled, PersonFilled } from "@fluentui/react-icons";
import { SkyText, SkyLoader, SkyTooltip, SkyBadge } from "@skypointcloud/skypoint-ui";
import { useLazyGetApplicantsQuery, Applicant } from "../../../../store/api/applicantsApi";
import { useLazyGetJobsQuery, JobPosting } from "../../../../store/api/jobsApi";
import { useMsalAuth } from "../../../../hooks/useMsalAuth";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useBooleanSearch } from "../../../../hooks/useBooleanSearch";
import { BooleanSearchHints } from "./BooleanSearchHints";
import { departmentFilterAppliesTo } from "./locationSwitcher";
import { SCOPE_URL_PARAMS } from "../../../../../shared/types/accessScope";
import { useLocationScope } from "../../../../components/shared/scope";
import { browserStorage } from "../../../../components/shared/scope/locationStore";
import {
  ALL,
  readStoredScope,
  resolveSelection,
  selectionLabels,
  toScopeParams,
} from "../../../../components/shared/scope/scopeFilterState";
import { usePageSearchIndex } from "../hooks/usePageSearchIndex";
import { searchPages, type PageSearchResult } from "../pageSearch";
import {
  parseBooleanQuery,
  extractTermsFromAST,
  evaluateBooleanAST,
} from "../../../../utils/booleanSearchParser";
import type { BooleanNode } from "../../../../utils/booleanSearchParser";

/**
 * The header's ink: the box border, the search icon and the "/" and "B" chips. Plain tokens whose
 * defaults are exactly the rgba(255, 255, 255, n) they replace (src/index.css); a product palette
 * re-points them (src/products/performance/theme.css). Not color-mix(), which older browsers drop.
 */
const HEADER_INK = {
  border: "var(--app-header-border)",
  icon: "var(--app-header-icon)",
  chipBorder: "var(--app-header-chip-border)",
  chip: "var(--app-header-chip)",
} as const;

/**
 * Where "/" belongs to whatever has focus, not to the search shortcut: anything typed into, and
 * anything inside an open dialog or popover. Dates need the "/". The DS date picker moves focus to
 * a plain button in its calendar popover (role="dialog") as soon as the field is clicked, so a
 * tag check alone let the shortcut fire there.
 */
const KEEPS_SLASH =
  "input, textarea, select, [role='textbox'], [role='combobox'], [role='dialog'], [role='alertdialog'], [aria-haspopup='dialog']";

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || el.closest(KEEPS_SLASH) !== null;
}

/**
 * `fluid` (Task 14 fix round 1): only beside the product switcher. The box then gives way from 360px
 * down to 160px so the desktop bar never overflows; without it the markup is the fixed 360px box.
 */
export function GlobalSearchBar({ fluid = false }: { fluid?: boolean } = {}) {
  const navigate = useNavigate();
  const { getInstanceId, getQueryString } = useMsalAuth();
  const instanceId = getInstanceId();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  // Search finds what the page around it lists: the header's Location on every
  // page, and the page's Department where the page has that filter, resolved as
  // the page resolves it (the URL first, then the remembered choice).
  const locationScope = useLocationScope(instanceId || "");
  const onDepartmentPage = departmentFilterAppliesTo(pathname);
  const urlDepartment = searchParams.get(SCOPE_URL_PARAMS.department);
  const scopeParams = useMemo(() => {
    const department = onDepartmentPage
      ? resolveSelection({
          urlLocation: null,
          urlDepartment,
          stored: readStoredScope(instanceId || "", browserStorage()),
          scope: locationScope.scope,
        }).department
      : ALL;
    return toScopeParams(locationScope.location, department);
  }, [onDepartmentPage, urlDepartment, instanceId, locationScope.scope, locationScope.location]);
  // Named in the results, so a short list reads as "in this community", not "all there is".
  const searchedLocation = selectionLabels(
    locationScope.scope,
    locationScope.location,
    ALL,
  ).location;

  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchBoxRef = useRef<HTMLInputElement>(null);

  const { isBooleanMode, toggleBooleanMode, meetsMinLength, syntaxError, setSyntaxError } =
    useBooleanSearch();

  const [triggerApplicantSearch, { data: applicantData, isFetching: applicantsFetching }] =
    useLazyGetApplicantsQuery();
  const [triggerJobSearch, { data: jobData, isFetching: jobsFetching }] = useLazyGetJobsQuery();

  // Boolean mode: track multi-term search results and loading state
  const [booleanApplicants, setBooleanApplicants] = useState<Applicant[]>([]);
  const [booleanJobs, setBooleanJobs] = useState<JobPosting[]>([]);
  const [booleanLoading, setBooleanLoading] = useState(false);
  const [booleanAST, setBooleanAST] = useState<BooleanNode | null>(null);

  const isFetching = isBooleanMode ? booleanLoading : applicantsFetching || jobsFetching;
  // Until the debounce catches up, the job and applicant data on hand answers
  // the previous query, so it is held back rather than shown under this one.
  // Records also wait for the caller's scope (the effect below does not search
  // before it), and "No results found" must not show in the meantime.
  const recordsPending = isFetching || debouncedQuery !== query || !locationScope.ready;

  // Pages need no request, so they answer the live query, not the debounced
  // one. Off in boolean mode, which is a syntax for searching records.
  const pageIndex = usePageSearchIndex();
  const pages = useMemo(
    () => (isBooleanMode ? [] : searchPages(pageIndex, query)),
    [isBooleanMode, pageIndex, query],
  );

  // Fire search when debounced query changes (min 2 chars of actual content)
  useEffect(() => {
    const hasMinLength = isBooleanMode
      ? meetsMinLength(debouncedQuery)
      : debouncedQuery.length >= 2;

    if (hasMinLength && instanceId) {
      // Nothing until the caller's scope is known, so a remembered location never
      // searches the whole instance first. The effect runs again once it is.
      if (!locationScope.ready) return;
      if (isBooleanMode) {
        // Parse and validate
        const result = parseBooleanQuery(debouncedQuery);
        if (!result.success || !result.ast) {
          setSyntaxError(result.error?.message ?? "Invalid query");
          setBooleanAST(null);
          setIsOpen(true);
          return;
        }
        setSyntaxError(null);
        setBooleanAST(result.ast);

        // Extract individual terms and search each one separately
        const terms = extractTermsFromAST(result.ast);
        if (terms.length === 0) return;

        setBooleanLoading(true);
        const ast = result.ast;

        // Fire parallel searches for each term, then merge & filter
        const applicantPromises = terms.map((term) =>
          triggerApplicantSearch({
            instanceId,
            search: term,
            limit: 50,
            page: 1,
            ...scopeParams,
          })
            .unwrap()
            .catch(() => ({
              data: [] as Applicant[],
              meta: { total: 0, page: 1, limit: 50, totalPages: 0 },
            })),
        );
        const jobPromises = terms.map((term) =>
          triggerJobSearch({
            search: term,
            limit: 50,
            page: 1,
            ...scopeParams,
          })
            .unwrap()
            .catch(() => ({
              data: [] as JobPosting[],
              meta: { total: 0, page: 1, limit: 50, totalPages: 0 },
            })),
        );

        Promise.all([Promise.all(applicantPromises), Promise.all(jobPromises)]).then(
          ([applicantResults, jobResults]) => {
            // Merge & deduplicate applicants
            const allApplicants = new Map<string, Applicant>();
            applicantResults.forEach((r) => r.data.forEach((a) => allApplicants.set(a.id, a)));

            // Merge & deduplicate jobs
            const allJobs = new Map<string, JobPosting>();
            jobResults.forEach((r) => r.data.forEach((j) => allJobs.set(j.id, j)));

            // Filter using boolean AST — match against all searchable fields including AI summary, location, dept
            const filteredApplicants = Array.from(allApplicants.values()).filter((a) => {
              const parts = [
                a.fullName,
                a.email,
                a.jobTitle,
                a.currentTitle,
                a.locationName,
                a.department,
              ];
              if (a.skills?.length) parts.push(...a.skills);
              if (a.tags?.length) parts.push(...a.tags);
              const searchText = parts.filter(Boolean).join(" ");
              return evaluateBooleanAST(ast, searchText);
            });
            const filteredJobs = Array.from(allJobs.values()).filter((j) => {
              const searchText = [j.title, j.department, j.locationName, j.industry, j.jobType]
                .filter(Boolean)
                .join(" ");
              return evaluateBooleanAST(ast, searchText);
            });

            setBooleanApplicants(filteredApplicants.slice(0, 10));
            setBooleanJobs(filteredJobs.slice(0, 10));
            setBooleanLoading(false);
          },
        );
      } else {
        // Normal search mode — single query
        // Common first/last names match many candidates; 5 rows truncated the real
        // match away, so give applicants a little more room than jobs.
        triggerApplicantSearch({
          instanceId,
          search: debouncedQuery,
          limit: 8,
          page: 1,
          ...scopeParams,
        });
        triggerJobSearch({
          search: debouncedQuery,
          limit: 5,
          page: 1,
          ...scopeParams,
        });
      }
      setIsOpen(true);
    } else {
      setIsOpen(false);
      setSyntaxError(null);
      setBooleanAST(null);
      setBooleanApplicants([]);
      setBooleanJobs([]);
    }
  }, [
    debouncedQuery,
    instanceId,
    isBooleanMode,
    triggerApplicantSearch,
    triggerJobSearch,
    meetsMinLength,
    setSyntaxError,
    locationScope.ready,
    scopeParams,
  ]);

  // Keyboard shortcut: Ctrl+K to focus, Escape to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === "k") {
        e.preventDefault();
        searchBoxRef.current?.focus();
      }
      if (e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey && !isInputFocused()) {
        e.preventDefault();
        searchBoxRef.current?.focus();
      }
      if (e.key === "Escape") {
        setIsOpen(false);
        searchBoxRef.current?.blur();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleApplicantClick = useCallback(
    (applicant: Applicant) => {
      setIsOpen(false);
      setQuery("");
      navigate(`/applicants/${applicant.id}${getQueryString()}`);
    },
    [navigate, getQueryString],
  );

  const handleJobClick = useCallback(
    (job: JobPosting) => {
      setIsOpen(false);
      setQuery("");
      navigate(`/jobs/${job.id}${getQueryString()}`);
    },
    [navigate, getQueryString],
  );

  const handlePageClick = useCallback(
    (page: PageSearchResult) => {
      setIsOpen(false);
      setQuery("");
      navigate(`${page.route}${getQueryString()}`);
    },
    [navigate, getQueryString],
  );

  const handleBooleanToggle = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      toggleBooleanMode();
    },
    [toggleBooleanMode],
  );

  const applicants = isBooleanMode ? booleanApplicants : (applicantData?.data ?? []);
  const jobs = isBooleanMode ? booleanJobs : (jobData?.data ?? []);
  const hasResults = pages.length > 0 || applicants.length > 0 || jobs.length > 0;

  return (
    <div
      ref={wrapperRef}
      className={
        fluid
          ? "relative flex w-[360px] min-w-[160px] shrink items-center justify-end"
          : "relative flex items-center justify-end"
      }
    >
      {/* Always-on fixed search box — no hover expand, no width animation */}
      <div className={`relative overflow-visible ${fluid ? "w-full" : "w-[360px]"}`}>
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              backgroundColor: "var(--app-header-field)",
              borderRadius: 6,
              border: `1px solid ${HEADER_INK.border}`,
              padding: "0 10px",
              height: 30,
              gap: 8,
            }}
          >
            <Search style={{ width: 16, height: 16, color: HEADER_INK.icon, flexShrink: 0 }} />
            <input
              ref={searchBoxRef}
              placeholder={isBooleanMode ? 'Boolean: e.g. nurse AND "full-time"' : "Search..."}
              value={query}
              onChange={(e) => {
                const value = e.target.value;
                setQuery(value);
                // Open as you type so page matches show without waiting on the
                // debounce. Boolean mode has no pages and keeps its old timing.
                if (!isBooleanMode) setIsOpen(value.length >= 2);
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              autoComplete="off"
              className="placeholder:text-header-placeholder!"
              style={{
                flex: 1,
                height: "100%",
                backgroundColor: "transparent",
                color: "var(--app-header-fg)",
                fontSize: 14,
                outline: "none",
                border: "none",
                boxShadow: "none",
                WebkitAppearance: "none",
                // Fluid only: a flex:1 <input> otherwise keeps its ~150px intrinsic minimum and
                // pushes the "/" hint and the "B" toggle past the box border below ~240px.
                ...(fluid ? { minWidth: 0 } : {}),
              }}
            />
            {!query && !isFocused && (
              <kbd
                aria-hidden="true"
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  borderRadius: 4,
                  padding: "0 6px",
                  lineHeight: "16px",
                  flexShrink: 0,
                  pointerEvents: "none",
                  border: `1px solid ${HEADER_INK.chipBorder}`,
                  color: "var(--app-header-hint)",
                }}
              >
                /
              </kbd>
            )}
            <SkyTooltip
              content={isBooleanMode ? "Disable boolean search" : "Enable boolean search"}
            >
              <button
                onClick={handleBooleanToggle}
                type="button"
                aria-pressed={isBooleanMode}
                aria-label="Toggle boolean search"
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  borderRadius: 4,
                  padding: "0 6px",
                  lineHeight: "16px",
                  cursor: "pointer",
                  flexShrink: 0,
                  border: isBooleanMode
                    ? "1px solid #ffb31c"
                    : `1px solid ${HEADER_INK.chipBorder}`,
                  backgroundColor: isBooleanMode ? "#ffb31c" : "transparent",
                  color: isBooleanMode ? "#ffffff" : HEADER_INK.chip,
                }}
              >
                B
              </button>
            </SkyTooltip>
          </div>

          {isOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-card text-[#242424] dark:text-foreground rounded-md shadow-lg border border-[#d1d1d1] dark:border-border max-h-[420px] overflow-y-auto z-[10001]">
              {/* Boolean hints bar */}
              {isBooleanMode && <BooleanSearchHints />}

              {searchedLocation && (
                <div className="px-3 pt-2">
                  <SkyText
                    size={200}
                    color="muted"
                  >
                    {`In ${searchedLocation}`}
                  </SkyText>
                </div>
              )}

              {/* Syntax error */}
              {isBooleanMode && syntaxError && (
                <div className="p-3 text-red-600 dark:text-red-400 text-xs text-center">
                  <SkyText className="text-xs">{syntaxError}</SkyText>
                </div>
              )}

              {/* Loading */}
              {!syntaxError && recordsPending && (
                <div className="flex justify-center p-4">
                  <SkyLoader
                    size="small"
                    label="Searching..."
                  />
                </div>
              )}

              {/* No results */}
              {!syntaxError && !recordsPending && !hasResults && (
                <div className="p-4 text-center text-[#616161] dark:text-muted-foreground">
                  <SkyText>No results found</SkyText>
                </div>
              )}

              {/* Job results */}
              {!syntaxError && !recordsPending && jobs.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1 border-b border-neutral-200 dark:border-border">
                    <BriefcaseFilled className="text-[#ffb31c] text-base" />
                    <SkyText className="font-semibold text-xs">Jobs</SkyText>
                  </div>
                  {jobs.map((job) => (
                    <div
                      key={job.id}
                      className="flex flex-col px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5"
                      onClick={() => handleJobClick(job)}
                      role="option"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleJobClick(job);
                      }}
                    >
                      <span className="font-semibold text-[#242424] dark:text-foreground">
                        {job.title}
                      </span>
                      <span className="text-[#616161] dark:text-muted-foreground text-xs">
                        {[job.jobCode, job.department, job.locationName, job.jobType]
                          .filter(Boolean)
                          .join(" · ")}
                        {job.isPublished ? " · Published" : " · Draft"}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Applicant results */}
              {!syntaxError && !recordsPending && applicants.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1 border-b border-neutral-200 dark:border-border">
                    <PersonFilled className="text-[#ffb31c] text-base" />
                    <SkyText className="font-semibold text-xs">Applicants</SkyText>
                  </div>
                  {applicants.map((applicant) => (
                    <div
                      key={applicant.id}
                      className="flex flex-col px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5"
                      onClick={() => handleApplicantClick(applicant)}
                      role="option"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleApplicantClick(applicant);
                      }}
                    >
                      <span className="flex items-center gap-1.5">
                        <span className="font-semibold text-[#242424] dark:text-foreground">
                          {applicant.fullName}
                        </span>
                        {applicant.status === "archived" && (
                          <SkyBadge
                            appearance="tint"
                            color="subtle"
                          >
                            Archived
                          </SkyBadge>
                        )}
                      </span>
                      <span className="text-[#616161] dark:text-muted-foreground text-xs">
                        {applicant.currentTitle || applicant.email}
                        {applicant.jobTitle && ` · ${applicant.jobTitle}`}
                        {applicant.skills?.length
                          ? ` · ${applicant.skills.slice(0, 3).join(", ")}`
                          : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Page results, last: jobs and applicants come first. */}
              {pages.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1 border-b border-neutral-200 dark:border-border">
                    <CompassNorthwestFilled className="text-[#ffb31c] text-base" />
                    <SkyText className="font-semibold text-xs">Pages</SkyText>
                  </div>
                  {pages.map((page) => (
                    <div
                      key={page.key}
                      className="flex flex-col px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5"
                      onClick={() => handlePageClick(page)}
                      role="option"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handlePageClick(page);
                      }}
                    >
                      <span className="font-semibold text-[#242424] dark:text-foreground">
                        {page.title}
                      </span>
                      {page.trail.length > 0 && (
                        <span className="text-[#616161] dark:text-muted-foreground text-xs">
                          {page.trail.join(" › ")}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
